import React, { useState, useEffect } from "react";
import LoginHeader from "./components/LoginHeader.js";
import AADHeader from "./components/AADHeader.js";
import SideNavigation from "./components/SideNavigation.js";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { appInsights } from "./AppInsights";

const FRAMEWORKS = [
  {
    key: "SASB",
    name: "SASB Standards",
    tagline: "Industry-specific, financially material disclosure topics",
    body:
      "The SASB Standards (now maintained by the IFRS Foundation's ISSB) identify the sustainability topics most likely to affect financial performance in each of 77 industries. Rather than reporting everything, you disclose the handful of metrics investors in your industry actually price in.",
    structure: [
      "77 industry standards across 11 sectors",
      "~6 disclosure topics and ~13 accounting metrics per industry",
      "Quantitative metrics with defined units and protocols",
    ],
    ecoprism:
      "Use your legal entity's industry sector to find your SASB industry, then map EcoPrism's energy, emissions, water and workforce data to the corresponding accounting metrics.",
    link: "https://sasb.ifrs.org/standards/",
  },
  {
    key: "TCFD",
    name: "TCFD Recommendations",
    tagline: "Climate risk through a financial lens",
    body:
      "The Task Force on Climate-related Financial Disclosures structures climate reporting around how climate risks and opportunities affect your business — not just your footprint. Its four pillars are now embedded in ESRS E1, ISSB S2 and most national climate-disclosure rules.",
    structure: [
      "Four pillars: Governance, Strategy, Risk Management, Metrics & Targets",
      "Eleven recommended disclosures across the pillars",
      "Scenario analysis against 1.5°C and higher-warming pathways",
    ],
    ecoprism:
      "EcoPrism's Scope 1, 2 and 3 dashboards and emission targets cover the Metrics & Targets pillar directly; governance and strategy narratives draw on your materiality assessment.",
    link: "https://www.fsb-tcfd.org/recommendations/",
  },
  {
    key: "CDP",
    name: "CDP Disclosure",
    tagline: "Scored environmental disclosure requested by customers and investors",
    body:
      "CDP is an annual questionnaire covering climate change, water security and forests, scored from A to F. Large buyers increasingly require suppliers to respond, making CDP the most common 'first ask' companies receive.",
    structure: [
      "Integrated questionnaire across climate, water and forests modules",
      "A–F scoring: Disclosure → Awareness → Management → Leadership",
      "Aligned with TCFD and increasingly with ISSB standards",
    ],
    ecoprism:
      "Your calculated Scope 1/2/3 inventory, energy mix, targets and reduction initiatives in EcoPrism map directly onto CDP's climate module (C5–C7 emissions sections in particular).",
    link: "https://www.cdp.net/en/companies/companies-scores",
  },
  {
    key: "ESRS",
    name: "ESRS (CSRD)",
    tagline: "The EU's mandatory sustainability reporting standards",
    body:
      "The European Sustainability Reporting Standards are what CSRD-scoped companies must report against — cross-cutting standards plus topical environmental, social and governance standards, filtered by a double materiality assessment.",
    structure: [
      "ESRS 1 & 2: general principles and general disclosures",
      "E1–E5: climate, pollution, water, biodiversity, circular economy",
      "S1–S4 social standards and G1 governance",
    ],
    ecoprism:
      "EcoPrism's CSRD module walks through materiality, data collection and report generation for ESRS directly — this is the framework with the deepest native support.",
    link: "https://www.efrag.org/en/sustainability-reporting",
  },
];

export default function FrameworkGuides() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  useEffect(() => {
    appInsights.trackPageView();
    const handleResize = () => setIsSidebarOpen(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const renderHeader = () => {
    const loginType = localStorage.getItem("loginType");
    if (loginType === "AAD") {
      return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
    }
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
  };

  return (
    <div id="wrapper">
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div
        id="page-content-wrapper"
        className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}
      >
        <div className="container formpd">
          <div className="col-md-12 flex mt-50">
            <div className="col-md-12 pd-0">
              <p className="home-head">Reporting Framework Guides</p>
              <p className="glossary-intro">
                Methodologies and alignment guidance for the major sustainability
                reporting frameworks, and how EcoPrism data maps to each.
              </p>
            </div>
          </div>

          <div className="col-md-12 pd-0 mb-25">
            {FRAMEWORKS.map((fw) => (
              <div className="framework-guide-card" key={fw.key}>
                <div className="framework-guide-head">
                  <span className="framework-chip framework-chip--lg">{fw.key}</span>
                  <div>
                    <p className="framework-guide-name">{fw.name}</p>
                    <p className="framework-guide-tagline">{fw.tagline}</p>
                  </div>
                </div>
                <p className="framework-guide-body">{fw.body}</p>
                <div className="framework-guide-cols">
                  <div>
                    <p className="framework-guide-subhead">Structure</p>
                    <ul className="framework-guide-list">
                      {fw.structure.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="framework-guide-subhead">Alignment with EcoPrism</p>
                    <p className="framework-guide-body mb-0">{fw.ecoprism}</p>
                  </div>
                </div>
                <a
                  href={fw.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="framework-guide-link"
                >
                  Official standard <OpenInNewIcon fontSize="inherit" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
